import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Download as DownloadIcon,
  Folder as FolderIcon,
  Home as HomeIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import JSZip from "jszip";
import React, { useEffect, useMemo, useState } from "react";
import { RendererProps } from "../FilePreviewModal";
import CustomScrollbars from "../../CustomScrollbars";
import {
  detectKind,
  formatBytes,
  getExtension,
  humanKindLabel,
  isPreviewable,
  PreviewKind,
} from "../previewTypes";
import { getFileIconForName } from "../previewIcons";
import { useFilePreview } from "../useFilePreview";

interface ZipEntry {
  path: string;
  name: string;
  isDir: boolean;
  size: number;
  kind: PreviewKind;
  extension: string;
}

interface ZipNode {
  name: string;
  path: string;
  isDir: boolean;
  size: number;
  kind: PreviewKind;
  extension: string;
  children: ZipNode[];
}

const MAX_TOTAL_UNCOMPRESSED = 100 * 1024 * 1024;

const buildTree = (entries: ZipEntry[]): ZipNode => {
  const root: ZipNode = {
    name: "",
    path: "",
    isDir: true,
    size: 0,
    kind: "unknown",
    extension: "",
    children: [],
  };

  const ensureDir = (parts: string[]): ZipNode => {
    let cursor = root;
    let pathSoFar = "";
    for (const part of parts) {
      pathSoFar = pathSoFar ? `${pathSoFar}/${part}` : part;
      let next = cursor.children.find(
        (c) => c.isDir && c.name === part,
      );
      if (!next) {
        next = {
          name: part,
          path: pathSoFar,
          isDir: true,
          size: 0,
          kind: "unknown",
          extension: "",
          children: [],
        };
        cursor.children.push(next);
      }
      cursor = next;
    }
    return cursor;
  };

  for (const entry of entries) {
    const parts = entry.path.split("/").filter(Boolean);
    if (entry.isDir) {
      ensureDir(parts);
      continue;
    }
    if (parts.length === 0) continue;
    const fileName = parts[parts.length - 1];
    const dir = parts.length > 1 ? ensureDir(parts.slice(0, -1)) : root;
    dir.children.push({
      name: fileName,
      path: entry.path,
      isDir: false,
      size: entry.size,
      kind: entry.kind,
      extension: entry.extension,
      children: [],
    });
  }

  return root;
};

const findNode = (root: ZipNode, path: string): ZipNode | null => {
  if (!path) return root;
  const parts = path.split("/").filter(Boolean);
  let cursor: ZipNode = root;
  for (const part of parts) {
    const next = cursor.children.find((c) => c.name === part);
    if (!next) return null;
    cursor = next;
  }
  return cursor;
};

type SortMode = "name_asc" | "name_desc" | "size_asc" | "size_desc" | "type_asc";

const sortNodes = (nodes: ZipNode[], mode: SortMode): ZipNode[] => {
  const dirs = nodes.filter((n) => n.isDir);
  const files = nodes.filter((n) => !n.isDir);
  const cmpName = (a: ZipNode, b: ZipNode) =>
    a.name.localeCompare(b.name, undefined, {
      sensitivity: "base",
      numeric: true,
    });
  const cmp = (a: ZipNode, b: ZipNode) => {
    switch (mode) {
      case "name_desc":
        return -cmpName(a, b);
      case "size_asc":
        return a.size - b.size || cmpName(a, b);
      case "size_desc":
        return b.size - a.size || cmpName(a, b);
      case "type_asc":
        return (
          a.extension.localeCompare(b.extension, undefined, {
            sensitivity: "base",
          }) || cmpName(a, b)
        );
      case "name_asc":
      default:
        return cmpName(a, b);
    }
  };
  dirs.sort(cmp);
  files.sort(cmp);
  return [...dirs, ...files];
};

const ZipRenderer: React.FC<RendererProps> = ({ blob, fileName }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [zip, setZip] = useState<JSZip | null>(null);
  const [tree, setTree] = useState<ZipNode | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [warn, setWarn] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [path, setPath] = useState("");
  const [sortMode, setSortMode] = useState<SortMode>("name_asc");
  const [search, setSearch] = useState("");

  const { openInlinePreview } = useFilePreview();

  useEffect(() => {
    if (!blob) return;
    const fileBlob = blob;
    let cancelled = false;
    const run = async () => {
      try {
        setLoading(true);
        const loaded = await JSZip.loadAsync(fileBlob);
        if (cancelled) return;

        const entries: ZipEntry[] = [];
        let totalUncompressed = 0;
        loaded.forEach((relativePath, file) => {
          const isDir = !!file.dir;
          const cleanPath = relativePath.replace(/\/+$/, "");
          if (!cleanPath) return;
          const parts = cleanPath.split("/");
          const fname = parts[parts.length - 1] || cleanPath;
          const ext = getExtension(fname);
          const size =
            (file as unknown as { _data?: { uncompressedSize?: number } })
              ._data?.uncompressedSize ?? 0;
          totalUncompressed += size;
          entries.push({
            path: cleanPath,
            name: fname,
            isDir,
            size,
            kind: isDir ? "unknown" : detectKind(fname),
            extension: ext,
          });
        });

        if (totalUncompressed > MAX_TOTAL_UNCOMPRESSED) {
          setWarn(
            `Archive contents are ${formatBytes(totalUncompressed)}; preview is limited to ${formatBytes(
              MAX_TOTAL_UNCOMPRESSED,
            )}. Some entries may fail to preview.`,
          );
        } else {
          setWarn(null);
        }

        setZip(loaded);
        setTree(buildTree(entries));
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Failed to read archive");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [blob]);

  const currentNode = useMemo(() => {
    if (!tree) return null;
    return findNode(tree, path);
  }, [tree, path]);

  const visibleChildren = useMemo(() => {
    if (!currentNode) return [];
    let nodes = currentNode.children;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      nodes = nodes.filter((n) => n.name.toLowerCase().includes(q));
    }
    return sortNodes(nodes, sortMode);
  }, [currentNode, search, sortMode]);

  const breadcrumbParts = useMemo(() => {
    const parts = path.split("/").filter(Boolean);
    const result: { label: string; target: string }[] = [
      { label: fileName, target: "" },
    ];
    let acc = "";
    for (const p of parts) {
      acc = acc ? `${acc}/${p}` : p;
      result.push({ label: p, target: acc });
    }
    return result;
  }, [path, fileName]);

  const displayBreadcrumbs = useMemo(() => {
    const all = breadcrumbParts;
    const maxVisible = 3;
    if (all.length <= maxVisible) {
      return all.map((p) => ({ kind: "normal" as const, ...p }));
    }
    return [
      { kind: "normal" as const, ...all[0] },
      { kind: "ellipsis" as const },
      ...all.slice(-2).map((p) => ({ kind: "normal" as const, ...p })),
    ];
  }, [breadcrumbParts]);

  const primaryNameSx = useMemo(
    () =>
      isMobile
        ? {
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            whiteSpace: "normal",
            wordBreak: "break-word",
            lineHeight: 1.35,
            fontWeight: 500,
          }
        : {
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontWeight: 500,
          },
    [isMobile],
  );

  const secondaryMetaSx = {
    fontSize: 12,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  } as const;

  const listRowSx = {
    alignItems: "flex-start" as const,
    borderBottom: "1px solid",
    borderColor: "divider",
    py: isMobile ? 0.5 : undefined,
    px: isMobile ? 1.5 : undefined,
    pr: isMobile ? 1.5 : 2,
    "& .MuiListItemText-root": {
      minWidth: 0,
      flex: "1 1 auto",
      mr: 1,
    },
    "& .MuiListItemText-primary": primaryNameSx,
    "& .MuiListItemText-secondary": secondaryMetaSx,
  };

  const handleDownloadFullArchive = () => {
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const extractAsBlob = async (entryPath: string): Promise<Blob | null> => {
    if (!zip) return null;
    const entry = zip.file(entryPath);
    if (!entry) return null;
    return entry.async("blob");
  };

  const handleDownloadEntry = async (node: ZipNode) => {
    const innerBlob = await extractAsBlob(node.path);
    if (!innerBlob) return;
    const url = URL.createObjectURL(innerBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = node.name;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const handlePreviewEntry = async (node: ZipNode) => {
    if (!isPreviewable(node.kind)) return;
    const innerBlob = await extractAsBlob(node.path);
    if (!innerBlob) return;
    openInlinePreview(node.name, innerBlob, {
      size: node.size,
      backLabel: "Back to archive",
    });
  };

  const handleFolderOpen = (node: ZipNode) => {
    if (!node.isDir) return;
    setPath(node.path);
    setSearch("");
  };

  if (loading) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          p: 4,
        }}
      >
        <CircularProgress size={28} />
        <Typography variant="body2" color="text.secondary">
          Reading archive…
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!tree) return null;

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "center" },
          gap: 1,
          px: 2,
          py: 1.25,
          borderBottom: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
        }}
      >
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          sx={{ flex: 1, minWidth: 0 }}
        >
          {path && (
            <Tooltip title="Back">
              <IconButton
                size="small"
                onClick={() => {
                  const parts = path.split("/").filter(Boolean);
                  parts.pop();
                  setPath(parts.join("/"));
                  setSearch("");
                }}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Breadcrumbs
            separator="/"
            sx={{
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              "& .MuiBreadcrumbs-ol": {
                flexWrap: "nowrap",
                overflow: "hidden",
              },
              "& .MuiBreadcrumbs-li": {
                maxWidth: "100%",
                overflow: "hidden",
                display: "flex",
              },
              "& .MuiBreadcrumbs-separator": {
                mx: 0.5,
                color: "text.disabled",
                flexShrink: 0,
              },
            }}
          >
            {displayBreadcrumbs.map((part, idx) => {
              if (part.kind === "ellipsis") {
                return (
                  <Typography
                    key="breadcrumb-ellipsis"
                    component="span"
                    color="text.secondary"
                    sx={{ flexShrink: 0, fontWeight: "normal", fontSize: 13 }}
                  >
                    …
                  </Typography>
                );
              }
              const isLast = idx === displayBreadcrumbs.length - 1;
              const showHome = part.target === "";
              return (
                <Link
                  key={`${part.target}-${idx}`}
                  component="button"
                  type="button"
                  underline="hover"
                  color={isLast ? "text.primary" : "text.secondary"}
                  onClick={() => {
                    if (!isLast) {
                      setPath(part.target);
                      setSearch("");
                    }
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    fontWeight: isLast ? 700 : 500,
                    fontSize: 13,
                    cursor: isLast ? "default" : "pointer",
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    border: "none",
                    background: "none",
                    fontFamily: "inherit",
                    padding: 0,
                    textAlign: "left",
                    "&:hover": {
                      color: isLast ? undefined : "primary.main",
                    },
                  }}
                >
                  {showHome && (
                    <HomeIcon fontSize="small" sx={{ flexShrink: 0 }} />
                  )}
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontWeight: "inherit",
                      fontSize: 13,
                    }}
                  >
                    {part.label}
                  </Typography>
                </Link>
              );
            })}
          </Breadcrumbs>
        </Stack>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", sm: "center" }}
          flexWrap={{ sm: "wrap" }}
          useFlexGap
          sx={{
            width: { xs: "100%", md: "auto" },
            flex: { md: 1 },
            minWidth: 0,
          }}
        >
          <TextField
            size="small"
            placeholder="Search in archive"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: "100%", sm: "auto" },
              flex: { sm: "1 1 160px" },
              minWidth: { xs: 0, sm: 140 },
            }}
          />
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ xs: "stretch", sm: "center" }}
            sx={{
              width: { xs: "100%", sm: "auto" },
              flexShrink: 0,
            }}
          >
            <Select
              size="small"
              value={sortMode}
              onChange={(e) => setSortMode(e.target.value as SortMode)}
              sx={{
                width: { xs: "100%", sm: "auto" },
                minWidth: { xs: 0, sm: 140 },
                fontSize: 13,
              }}
            >
              <MenuItem value="name_asc">Name (A → Z)</MenuItem>
              <MenuItem value="name_desc">Name (Z → A)</MenuItem>
              <MenuItem value="size_desc">Size (largest)</MenuItem>
              <MenuItem value="size_asc">Size (smallest)</MenuItem>
              <MenuItem value="type_asc">Type</MenuItem>
            </Select>
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadFullArchive}
              sx={{
                textTransform: "none",
                width: { xs: "100%", sm: "auto" },
                whiteSpace: { sm: "nowrap" },
              }}
            >
              Download archive
            </Button>
          </Stack>
        </Stack>
      </Box>

      {warn && (
        <Alert severity="warning" sx={{ borderRadius: 0 }}>
          {warn}
        </Alert>
      )}

      <CustomScrollbars
        sx={{
          flex: 1,
          minHeight: 0,
          backgroundColor: "background.paper",
        }}
      >
        {visibleChildren.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 4,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {search.trim()
                ? "No files match your search."
                : currentNode?.isDir && currentNode.children.length === 0
                  ? "This folder is empty."
                  : "Empty archive."}
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {visibleChildren.map((node) => {
              const previewable = !node.isDir && isPreviewable(node.kind);
              const FileTypeIcon = node.isDir
                ? null
                : getFileIconForName(node.name);

              const rowText = (
                <ListItemText
                  primary={node.name}
                  secondary={
                    node.isDir
                      ? `${node.children.length} item${
                          node.children.length !== 1 ? "s" : ""
                        }`
                      : `${humanKindLabel(node.kind, node.extension)}${
                          node.size > 0 ? ` • ${formatBytes(node.size)}` : ""
                        }${previewable ? "" : " • Preview not available"}`
                  }
                  primaryTypographyProps={{
                    sx: { flex: 1, minWidth: 0 },
                  }}
                />
              );

              const actionIcons = !node.isDir && (
                <Stack
                  direction="row"
                  spacing={0.25}
                  alignItems="flex-start"
                  sx={{ flexShrink: 0, pt: 0.25 }}
                >
                  {previewable && (
                    <Tooltip title="Preview">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          void handlePreviewEntry(node);
                        }}
                        aria-label={`Preview ${node.name}`}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Download">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        void handleDownloadEntry(node);
                      }}
                      aria-label={`Download ${node.name}`}
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              );

              if (node.isDir) {
                return (
                  <ListItem
                    key={node.path}
                    button
                    onClick={() => handleFolderOpen(node)}
                    sx={listRowSx}
                  >
                    <ListItemIcon sx={{ minWidth: 44 }}>
                      <FolderIcon sx={{ color: "primary.main" }} />
                    </ListItemIcon>
                    {rowText}
                  </ListItem>
                );
              }

              return (
                <ListItem key={node.path} sx={listRowSx}>
                  <ListItemIcon sx={{ minWidth: 44 }}>
                    {FileTypeIcon ? (
                      <FileTypeIcon
                        sx={{
                          color: previewable ? "primary.main" : "text.disabled",
                        }}
                      />
                    ) : null}
                  </ListItemIcon>
                  {rowText}
                  {actionIcons}
                </ListItem>
              );
            })}
          </List>
        )}
      </CustomScrollbars>
    </Box>
  );
};

export default ZipRenderer;
